import React, { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import { Loader, MapPin } from "lucide-react";
import api from "@/lib/api";

// Custom pickup pin (yellow diamond) and driver truck (orange square with truck emoji-icon)
const pickupIcon = L.divIcon({
  className: "",
  html: `<div style="width:32px;height:32px;background:#FFD700;border:3px solid #061224;transform:rotate(45deg);box-shadow:0 4px 12px rgba(0,0,0,0.4)"></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});
const driverIcon = L.divIcon({
  className: "",
  html: `<div style="width:44px;height:44px;background:#FF6B00;border:3px solid #FFF;border-radius:50%;box-shadow:0 4px 14px rgba(255,107,0,0.6);display:flex;align-items:center;justify-content:center;font-size:22px;">🛻</div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

function AutoFit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length < 1) return;
    if (points.length === 1) {
      map.setView(points[0], 14);
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
  }, [points, map]);
  return null;
}

export default function LiveTrackingMap({ booking, height = 340 }) {
  const [dl, setDl] = useState(null); // driver location + eta
  const [ticker, setTicker] = useState(0);
  const timerRef = useRef(null);

  const pickup = [booking.pickup_lat, booking.pickup_lng];

  useEffect(() => {
    if (!booking.driver_id) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/driver-location`);
        if (!stopped) setDl(data);
      } catch {}
    };
    tick();
    timerRef.current = setInterval(tick, 10000); // 10s polling for near-real-time feel
    return () => { stopped = true; if (timerRef.current) clearInterval(timerRef.current); };
  }, [booking.driver_id, booking.id]);

  // Local ETA counter — decrements every second so users see a live countdown between server polls
  useEffect(() => {
    const t = setInterval(() => setTicker((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const eta = useMemo(() => {
    if (!dl?.eta_minutes) return null;
    // Decrement estimated eta by seconds since we got it, so users see it counting down
    const started = dl.at ? new Date(dl.at).getTime() : Date.now();
    const elapsedMin = (Date.now() - started) / 60000;
    return Math.max(0, dl.eta_minutes - elapsedMin);
    // eslint-disable-next-line
  }, [dl, ticker]);

  const hasDriverGPS = dl?.has_location;
  const driverPos = hasDriverGPS ? [dl.lat, dl.lng] : null;
  const fitPoints = hasDriverGPS ? [pickup, driverPos] : [pickup];

  return (
    <div className="relative rounded-md overflow-hidden border-2 border-asap-deep" style={{ height }} data-testid="live-tracking-map">
      <MapContainer center={pickup} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={pickup} icon={pickupIcon}>
          <Popup><b>Your pickup</b><br/>{booking.pickup_address}</Popup>
        </Marker>
        {hasDriverGPS && (
          <>
            <Marker position={driverPos} icon={driverIcon}>
              <Popup>
                <b>{dl.driver_name}</b>
                {dl.vehicle && <div>{dl.vehicle}</div>}
                {dl.driver_rating != null && <div>★ {dl.driver_rating.toFixed(1)}</div>}
              </Popup>
            </Marker>
            <Polyline
              positions={[driverPos, pickup]}
              pathOptions={{ color: "#FF6B00", weight: 4, dashArray: "10,10", opacity: 0.85 }}
            />
          </>
        )}
        <AutoFit points={fitPoints}/>
      </MapContainer>

      {/* ETA banner */}
      <div className="absolute top-3 left-3 z-[500] bg-asap-deep text-white px-3 py-2 shadow-hard-sm border-2 border-asap-yellow" data-testid="eta-banner">
        {booking.driver_id ? (
          hasDriverGPS ? (
            <>
              <div className="text-[9px] uppercase tracking-widest text-asap-yellow font-bold">Driver ETA</div>
              <div className="font-display text-xl leading-none mt-0.5">
                {eta > 0 ? `~${Math.max(1, Math.round(eta))} min` : "Arriving now"}
              </div>
              <div className="text-[10px] text-white/80 mt-0.5">{dl.miles} mi away</div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-xs">
              <Loader size={12} className="animate-spin text-asap-yellow"/> Waiting for driver GPS…
            </div>
          )
        ) : (
          <div className="flex items-center gap-2 text-xs">
            <MapPin size={12} className="text-asap-yellow"/> Searching for a nearby driver…
          </div>
        )}
      </div>
    </div>
  );
}
